/**
 * Kleiner Hook, der bei einer WERTÄNDERUNG kurz eine Animations-Klasse aktiv
 * schaltet (für das Dashboard-Zahlen-Flash). Kein Polling, kein Dauer-Interval —
 * nur ein Timeout pro Änderung, sauber retriggert und beim Unmount aufgeräumt.
 *
 * Die reine Entscheidungslogik steckt in `nextPulseState` (testbar ohne DOM):
 * kein Puls beim Initial-Render, kein Puls bei gleichem Wert, Puls bei Änderung.
 */

import { useEffect, useRef } from "preact/hooks";
import { useSignal } from "@preact/signals";

export interface PulseState {
  value: unknown;
  seeded: boolean;
}

/** Anfangszustand: noch kein Wert gesehen (→ erstes Render löst keinen Puls aus). */
export function initialPulseState(): PulseState {
  return { value: undefined, seeded: false };
}

/**
 * Reiner Übergang: aus dem bisherigen Zustand + neuem Wert den neuen Zustand und
 * ob ein Puls ausgelöst werden soll ableiten.
 * - nicht geseedet (Initial-Render) → seeden, KEIN Puls
 * - gleicher Wert (`Object.is`) → KEIN Puls
 * - geänderter Wert → Puls
 */
export function nextPulseState(prev: PulseState, value: unknown): { state: PulseState; pulse: boolean } {
  if (!prev.seeded) return { state: { value, seeded: true }, pulse: false };
  if (Object.is(prev.value, value)) return { state: prev, pulse: false };
  return { state: { value, seeded: true }, pulse: true };
}

/** Gibt `true` für ~`ms` ms, sobald sich `value` ändert (nicht beim Initial-Render). */
export function useChangedPulse(value: unknown, ms = 700): boolean {
  const state = useRef<PulseState>(initialPulseState());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const on = useSignal(false);

  useEffect(() => {
    const { state: next, pulse } = nextPulseState(state.current, value);
    state.current = next;
    if (!pulse) return;
    on.value = true;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { on.value = false; }, ms);
  }, [value]);

  // Timer beim Unmount aufräumen.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return on.value;
}

/** Bequeme Klassen-Helfer-Variante. */
export function changedPulseClass(value: unknown, ms = 700): string {
  return useChangedPulse(value, ms) ? "value-changed" : "";
}
